import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const TRUSTPILOT_SCRIPT_ID = 'maurten-trustpilot-widget-bootstrap';
const TRUSTPILOT_SCRIPT_SRC = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
const TRUSTPILOT_WIDGET_ROOT = 'https://widget.trustpilot.com';

/**
 * @typedef {Object} TrustpilotWidgetApi
 * @property {(element: Element, forceReload?: boolean) => void} [loadFromElement]
 */

/**
 * @typedef {Object} TrustpilotVariantResource
 * @property {string} [sku]
 */

/**
 * @typedef {Object} TrustpilotVariantUpdateData
 * @property {Document | Element} [html]
 * @property {string | number} [productId]
 * @property {{ id?: string | number }} [newProduct]
 */

/**
 * @typedef {Object} TrustpilotVariantUpdateDetail
 * @property {TrustpilotVariantResource | null} [resource]
 * @property {TrustpilotVariantUpdateData} [data]
 */

/**
 * @typedef {Object} TrustpilotProductReviewsRefs
 * @property {HTMLElement} widget
 * @property {HTMLElement} [reviewsWidget]
 * @property {HTMLElement} [reviewsPanel]
 * @property {HTMLButtonElement} [reviewsToggle]
 */

/**
 * Keeps a Trustpilot product review TrustBox in sync with the selected variant SKU.
 *
 * @extends {Component<TrustpilotProductReviewsRefs>}
 */
class MaurtenTrustpilotProductReviews extends Component {
  /** @type {string[]} */
  requiredRefs = ['widget'];

  /** @type {Element | null} */
  variantUpdateTarget = null;

  /** @type {number} */
  reviewAvailabilityRequestId = 0;

  /** @type {boolean} */
  pendingReviewAvailabilityCheck = false;

  connectedCallback() {
    super.connectedCallback();

    this.variantUpdateTarget = this.getVariantUpdateTarget();
    this.variantUpdateTarget?.addEventListener(ThemeEvents.variantUpdate, this.updateForVariant);
    this.refs.reviewsToggle?.addEventListener('click', this.toggleReviews);
    this.renderTrustBox();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.variantUpdateTarget?.removeEventListener(ThemeEvents.variantUpdate, this.updateForVariant);
    this.refs.reviewsToggle?.removeEventListener('click', this.toggleReviews);
    this.reviewAvailabilityRequestId += 1;
  }

  getVariantUpdateTarget() {
    return this.closest('[id*="ProductInformation-"], featured-product-information, dialog, product-card');
  }

  /**
   * @param {Event} event
   */
  updateForVariant = (event) => {
    if (!(event instanceof CustomEvent)) return;

    const detail = /** @type {TrustpilotVariantUpdateDetail} */ (event.detail);
    const eventData = detail.data;
    const variant = detail.resource;

    if (!eventData) return;

    if (eventData.newProduct) {
      this.dataset.productId = String(eventData.newProduct.id);
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const html = eventData.html;
    const newBlock =
      html && typeof html.querySelector === 'function'
        ? html.querySelector(`maurten-trustpilot-product-reviews[data-block-id="${this.dataset.blockId}"]`)
        : null;

    if (newBlock instanceof HTMLElement) {
      this.dataset.productId = newBlock.dataset.productId || this.dataset.productId;
      this.dataset.productName = newBlock.dataset.productName || this.dataset.productName;
      this.dataset.productUrl = newBlock.dataset.productUrl || this.dataset.productUrl;
      this.dataset.currentSku = newBlock.dataset.currentSku || '';
      this.dataset.skuAllowlist = newBlock.dataset.skuAllowlist || this.dataset.skuAllowlist || '';
    } else {
      this.dataset.currentSku = variant?.sku || '';
    }

    this.renderTrustBox();
  };

  renderTrustBox() {
    const sku = (this.dataset.currentSku || '').trim();

    if (!sku || !this.skuIsAllowed(sku)) {
      this.hidden = true;
      this.refs.widget.innerHTML = '';
      this.refs.reviewsWidget?.replaceChildren();
      this.setExpandableControlsVisible(false);
      return;
    }

    this.pendingReviewAvailabilityCheck = Boolean(this.refs.reviewsToggle);
    this.hidden = false;
    this.setExpandableControlsVisible(false);
    this.refs.widget.innerHTML = '';
    this.setTrustpilotAttributes(this.refs.widget, sku, {
      templateId: this.dataset.templateId || '',
      styleHeight: this.dataset.styleHeight || '700px',
    });

    this.loadTrustpilot()
      .then(() => {
        getTrustpilot()?.loadFromElement?.(this.refs.widget, true);
        this.updateExpandableControlsForSku(sku);
      })
      .catch((error) => {
        if (this.pendingReviewAvailabilityCheck) {
          this.hidden = true;
        }

        console.warn(error);
      });
  }

  toggleReviews = () => {
    const panel = this.refs.reviewsPanel;
    const toggle = this.refs.reviewsToggle;

    if (!panel || !toggle) return;

    const isExpanded = panel.hidden;
    panel.hidden = !isExpanded;
    toggle.setAttribute('aria-expanded', String(isExpanded));

    const labels = toggle.querySelectorAll('.maurten-trustpilot-product-reviews__toggle-label');
    labels.forEach((label, index) => {
      if (label instanceof HTMLElement) {
        label.hidden = index === 0 ? isExpanded : !isExpanded;
      }
    });

    if (isExpanded) {
      this.renderExpandedReviews((this.dataset.currentSku || '').trim());
    }
  };

  /**
   * @param {string} sku
   */
  renderExpandedReviews(sku) {
    const panel = this.refs.reviewsPanel;
    const reviewsWidget = this.refs.reviewsWidget;

    if (!panel || !reviewsWidget || panel.hidden || !sku || !this.skuIsAllowed(sku)) return;

    reviewsWidget.innerHTML = '';
    this.setTrustpilotAttributes(reviewsWidget, sku, {
      templateId: this.dataset.reviewsTemplateId || '',
      styleHeight: this.dataset.reviewsStyleHeight || '700px',
    });

    this.loadTrustpilot()
      .then(() => {
        getTrustpilot()?.loadFromElement?.(reviewsWidget, true);
      })
      .catch((error) => {
        console.warn(error);
      });
  }

  /**
   * @param {string} sku
   */
  updateExpandableControlsForSku(sku) {
    const reviewsTemplateId = this.dataset.reviewsTemplateId || '';
    const businessUnitId = this.getAttribute('data-businessunit-id') || '';

    if (!this.refs.reviewsToggle || !sku || !reviewsTemplateId || !businessUnitId) {
      this.setExpandableControlsVisible(false);
      return;
    }

    const requestId = (this.reviewAvailabilityRequestId += 1);
    this.pendingReviewAvailabilityCheck = true;
    const reviewLanguages = this.dataset.reviewLanguages || '';
    const params = new URLSearchParams({
      sku,
      numberOfReviews: '1',
      productName: this.dataset.productName || '',
      templateId: reviewsTemplateId,
      url: `${window.location.origin}${window.location.pathname}`,
    });

    if (reviewLanguages) {
      params.set('reviewLanguages', reviewLanguages);
    }

    fetch(`${TRUSTPILOT_WIDGET_ROOT}/data/jsonld/business-unit/${businessUnitId}/product?${params}`)
      .then((response) => {
        if (!response.ok) return null;
        return response.json();
      })
      .then((data) => {
        if (requestId !== this.reviewAvailabilityRequestId) return;

        const hasReviews = Boolean(data?.aggregateRating || data?.review);
        this.pendingReviewAvailabilityCheck = false;
        this.hidden = !hasReviews;
        this.setExpandableControlsVisible(hasReviews);
      })
      .catch(() => {
        if (requestId === this.reviewAvailabilityRequestId) {
          this.pendingReviewAvailabilityCheck = false;
          this.hidden = true;
          this.setExpandableControlsVisible(false);
        }
      });
  }

  /**
   * @param {boolean} isVisible
   */
  setExpandableControlsVisible(isVisible) {
    const panel = this.refs.reviewsPanel;
    const toggle = this.refs.reviewsToggle;

    if (!toggle) return;

    toggle.hidden = !isVisible;

    if (!isVisible) {
      toggle.setAttribute('aria-expanded', 'false');
      if (panel) {
        panel.hidden = true;
      }

      const labels = toggle.querySelectorAll('.maurten-trustpilot-product-reviews__toggle-label');
      labels.forEach((label, index) => {
        if (label instanceof HTMLElement) {
          label.hidden = index !== 0;
        }
      });
    }
  }

  /**
   * @param {string} sku
   * @returns {boolean}
   */
  skuIsAllowed(sku) {
    const skuAllowlist = (this.dataset.skuAllowlist || '')
      .split(',')
      .map((allowlistedSku) => allowlistedSku.trim())
      .filter(Boolean);

    return skuAllowlist.length === 0 || skuAllowlist.includes(sku);
  }

  /**
   * @param {HTMLElement} widget
   * @param {string} sku
   * @param {{ templateId?: string, styleHeight?: string }} [options]
   */
  setTrustpilotAttributes(widget, sku, options = {}) {
    const reviewLanguages = this.dataset.reviewLanguages || '';
    const noReviews = this.dataset.noReviews || '';
    const token = this.dataset.token || '';
    const scrollToList = this.dataset.scrollToList || '';

    widget.className = 'trustpilot-widget';
    widget.setAttribute('data-locale', this.dataset.locale || 'en-US');
    widget.setAttribute('data-template-id', options.templateId || '');
    widget.setAttribute('data-businessunit-id', this.getAttribute('data-businessunit-id') || '');
    widget.setAttribute('data-style-height', options.styleHeight || '700px');
    widget.setAttribute('data-style-width', this.dataset.styleWidth || '100%');
    widget.setAttribute('data-theme', this.dataset.theme || 'light');
    widget.setAttribute('data-style-alignment', this.dataset.styleAlignment || 'center');
    widget.setAttribute('data-sku', sku);
    widget.setAttribute('data-name', this.dataset.productName || '');
    widget.setAttribute('data-url', this.dataset.productUrl || '');

    if (token) {
      widget.setAttribute('data-token', token);
    } else {
      widget.removeAttribute('data-token');
    }

    if (reviewLanguages) {
      widget.setAttribute('data-review-languages', reviewLanguages);
    } else {
      widget.removeAttribute('data-review-languages');
    }

    if (scrollToList) {
      widget.setAttribute('data-scroll-to-list', scrollToList);
    } else {
      widget.removeAttribute('data-scroll-to-list');
    }

    if (noReviews) {
      widget.setAttribute('data-no-reviews', noReviews);
    } else {
      widget.removeAttribute('data-no-reviews');
    }
  }

  /**
   * @returns {Promise<void>}
   */
  loadTrustpilot() {
    if (getTrustpilot()?.loadFromElement) {
      return Promise.resolve();
    }

    const existingScript = document.getElementById(TRUSTPILOT_SCRIPT_ID);

    if (existingScript) {
      if (existingScript.dataset.loaded === 'true') {
        return Promise.resolve();
      }

      return new Promise((resolve, reject) => {
        existingScript.addEventListener(
          'load',
          () => {
            existingScript.dataset.loaded = 'true';
            resolve();
          },
          { once: true }
        );
        existingScript.addEventListener('error', () => reject(new Error('Unable to load Trustpilot widget script.')), {
          once: true,
        });
      });
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.id = TRUSTPILOT_SCRIPT_ID;
      script.src = TRUSTPILOT_SCRIPT_SRC;
      script.async = true;
      script.addEventListener(
        'load',
        () => {
          script.dataset.loaded = 'true';
          resolve();
        },
        { once: true }
      );
      script.addEventListener('error', () => reject(new Error('Unable to load Trustpilot widget script.')), {
        once: true,
      });
      document.head.append(script);
    });
  }
}

/**
 * @returns {TrustpilotWidgetApi | undefined}
 */
function getTrustpilot() {
  return /** @type {Window & { Trustpilot?: TrustpilotWidgetApi }} */ (window).Trustpilot;
}

if (!customElements.get('maurten-trustpilot-product-reviews')) {
  customElements.define('maurten-trustpilot-product-reviews', MaurtenTrustpilotProductReviews);
}
